// Import dependencies
import initialiser from '../../utils/initialiser';

const defaultSettings = {
  openClass: 'is-open',
  activeClass: 'is-active',
  overlayClass: 'has-overlay',
  addToWishlistHtml: '<span>Add to wishlist</span>',
  removeFromWishlistHtml: '<span>Remove from wishlist</span>',
};

class WishlistManager {
  constructor(config) {
    const { settings } = config;

    // Set settings
    this.settings = { ...defaultSettings, ...settings };

    // Initial values
    this.wishlist = [];
    this.entryId = 0;
    this.concertPage = false;
    this.concertAdded = false;
    this.navIcons = document.querySelectorAll(this.settings.navIcons);
    this.clearWishlistBtn = undefined;
    this.wishlistPageContainer = undefined;
    this.removeFromWishlistBtns = undefined;
    this.activeDrawer = undefined;
    this.wishlistDrawers = undefined;
    this.openWishlistBtns = undefined;
    this.wishlistEmpty = undefined;
    this.wishlistItems = undefined;
    this.reactCal = undefined;
    this.now = new Date();
    this.aYearFromNow = this.now.setFullYear(this.now.getFullYear() + 1);

    // Determine if this is a concert detail page button
    this.concertDetailPageBtns = document.querySelectorAll(this.settings.concertButtonClass);
    if (this.concertDetailPageBtns.length > 0) {
      this.entryId = this.concertDetailPageBtns[0].dataset.entryId;
      this.concertPage = true;
    } else {
      this.isWishlistPage = document.querySelector(this.settings.wishlistPage);
      if (this.isWishlistPage) {
        // Query elements on the wishlist page
        this.clearWishlistBtn = this.isWishlistPage.querySelector(this.settings.clearWishlistButtonClass);
        this.removeFromWishlistBtns = this.isWishlistPage.querySelectorAll(this.settings.removeFromWishlist);
        this.openWishlistBtns = this.isWishlistPage.querySelectorAll(this.settings.openWishlist);
        this.wishlistDrawers = this.isWishlistPage.querySelectorAll(this.settings.wishlistDrawer);
        this.wishlistEmpty = this.isWishlistPage.querySelector(this.settings.wishlistEmpty);
        this.wishlistItems = this.isWishlistPage.querySelectorAll(this.settings.wishlistItem);
      }
    }
  }

  // Initialisation Function - Called on each component instance intialisation
  init = () => {
    this.bindEvents();
    this.getCurrentWishlist();
  };

  // Bind all class event listeners to the respective events
  bindEvents = () => {
    if (this.concertPage) this.concertDetailPageBtns.forEach((btn) => btn.addEventListener('click', this.handleConcertButtonClick));
    if (this.isWishlistPage) {
      this.clearWishlistBtn.addEventListener('click', () => {
        this.clearWishlist();
        this.modifyReactWishlistState(-1, true);
      });
      this.removeFromWishlistBtns.forEach((button) => button.addEventListener('click', (e) => {
        this.removeFromWishlist(button.dataset.entryId, button, e);
        this.modifyReactWishlistState(button.dataset.entryId, true);
      }));
      this.openWishlistBtns.forEach((el) => el.addEventListener('click', this.openWishlist));
    }
  };

  // Set concert detail page button text
  setConcertDetailPageButtonText = () => {
    if (!this.wishlist || !this.wishlist.find((id) => id === this.entryId)) {
      this.concertAdded = false;
      this.concertDetailPageBtns.forEach((btn) => {
        btn.innerHTML = this.settings.addToWishlistHtml;
        btn.classList.remove('is-wishlist');
      });
    } else {
      this.concertAdded = true;
      this.concertDetailPageBtns.forEach((btn) => {
        btn.innerHTML = this.settings.removeFromWishlistHtml;
        btn.classList.add('is-wishlist');
      });
    }
  };

  // Get current wishlist cookie value
  getCurrentWishlist = () => {
    if (document.cookie.split(';')
      .some((item) => item.trim()
        .startsWith(this.settings.cookieKey))) {
      document.cookie.split(';')
        .filter((key) => (key.trim()
          .startsWith(this.settings.cookieKey)))[0].split('=')[1].split(',')
        .forEach((id) => {
          if (id) this.wishlist.push(id);
        });
    }

    if (this.concertPage) {
      this.setConcertDetailPageButtonText();
    }

    if (this.wishlist.length > 0) {
      this.toggleNavIcons();

      if (this.isWishlistPage) {
        this.clearWishlistBtn.classList.remove('u-hide');
      }
    }
  };

  openWishlist = (e) => {
    e.preventDefault();

    // find drawer
    const drawer = Array.prototype.slice.call(this.wishlistDrawers)
      .find((el) => el.dataset.entryId === e.currentTarget.dataset.entryId);

    if (!drawer) return;

    // close other drawers
    this.wishlistDrawers.forEach((el) => el.classList.remove(this.settings.openClass));

    document.body.classList.add(this.settings.overlayClass);
    drawer.classList.add(this.settings.openClass);

    // bind events
    drawer.querySelector(this.settings.closeWishlist)
      .addEventListener('click', this.closeWishlist);
    document.addEventListener('keyup', this.handleKeyUp);
    // after transition
    setTimeout(() => {
      document.body.addEventListener('click', this.handleBodyClick);
    }, 300);

    this.activeDrawer = drawer;
  };

  closeWishlist = (e) => {
    e.preventDefault();

    document.body.classList.remove(this.settings.overlayClass);
    this.activeDrawer.classList.remove(this.settings.openClass);

    // unbind events
    e.currentTarget.removeEventListener('click', this.closeWishlist);
    document.removeEventListener('keyup', this.handleKeyUp);
    document.body.removeEventListener('click', this.handleBodyClick);
  };

  handleKeyUp = (e) => {
    if (e.key === 'Escape' || e.keyCode === 27) {
      this.closeWishlist(e);
    }
  };

  handleBodyClick = (e) => {
    // if the overlay is clicked, close the wishlist
    if (!this.activeDrawer.contains(e.target)) {
      this.closeWishlist(e);
    }
  };

  toggleNavIcons = (show = true) => {
    this.navIcons.forEach((el) => {
      if (show) {
        el.classList.add(this.settings.activeClass);
      } else {
        el.classList.remove(this.settings.activeClass);
      }
    });
  };

  clearWishlist = () => {
    this.wishlist = [];
    document.cookie = `${this.settings.cookieKey}; expires=${this.now}; path=/`;
    this.toggleNavIcons(false);

    if (this.isWishlistPage) {
      this.clearWishlistBtn.classList.add('u-hide');
      this.wishlistItems.forEach((el) => el.remove());
      this.wishlistEmpty.classList.remove('u-hide');
    }
  };

  addToWishlist = (entryId = null) => {
    if (entryId) this.entryId = entryId;
    if (!this.wishlist.includes(this.entryId)) {
      this.wishlist.push(this.entryId);
      document.cookie = `${this.settings.cookieKey}${this.wishlist.join(',')}; expires=${this.aYearFromNow}; path=/`;
    }

    this.setConcertDetailPageButtonText();

    this.toggleNavIcons();
  };

  removeFromWishlist = (entryId = this.entryId, button = undefined, e = undefined) => {
    if (e) e.preventDefault();

    if (this.wishlist.length === 1) {
      this.clearWishlist();
    } else {
      const index = this.wishlist.indexOf(entryId);

      this.wishlist.splice(index, 1);

      document.cookie = `${this.settings.cookieKey}${this.wishlist.join(',')}; expires=${this.aYearFromNow}; path=/`;

      if (this.isWishlistPage) {
        button.closest(this.settings.wishlistItem)
          .remove();
      }
    }

    this.setConcertDetailPageButtonText();
  };

  modifyReactWishlistState = (entryId, remove = false) => {
    if (!this.reactCal) this.reactCal = window[this.settings.reactRef];
    if (!this.reactCal) return;

    if (remove && entryId === -1) {
      // Clear state
      this.reactCal.setState(() => ({
        wishlist: [],
      }));
    } else if (remove && this.reactCal.state.wishlist.includes(entryId)) {
      this.reactCal.setState((prev) => ({
        wishlist: prev.wishlist.filter((v) => v !== entryId),
      }));
    } else if (!this.reactCal.state.wishlist.includes(entryId)) {
      this.reactCal.setState((prev) => ({
        wishlist: [...prev.wishlist.filter((v) => v !== entryId), entryId],
      }));
    }
  };

  handleConcertButtonClick = (e = undefined) => {
    if (!this.concertAdded) {
      this.addToWishlist();
      this.modifyReactWishlistState(this.entryId, false);
    } else {
      this.removeFromWishlist(this.entryId, e.currentTarget || undefined, e);
      this.modifyReactWishlistState(this.entryId, true);
    }
  };
}

// Export component wrapped in initialiser utility
export default (settings) => initialiser(settings, WishlistManager);
